import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle, ArrowDownToLine, ArrowLeftRight, BadgeDollarSign, CheckCircle2,
  CircleGauge, Clock3, Copy, Database, ExternalLink, Landmark, LockKeyhole, Network,
  ReceiptText, RefreshCcw, Rocket, ShieldAlert, UsersRound
} from "lucide-react";
import { useMemo, useState } from "react";
import type { CSSProperties, FormEvent } from "react";
import { PrimaryButton, SecondaryButton, StatusPill } from "@ocs/ui";
import { adminApi } from "../api";
import { PaginationBar } from "../components/PaginationBar";
import { amount } from "../format";

const PRINCIPAL_PAGE_SIZE = 20;
const PAYOUT_FLOW_PAGE_SIZE = 20;

type Plan = {
  product_code: "MARKET_7D" | "MARKET_90D" | "MARKET_180D";
  product_name: string;
  lock_days: number;
  min_daily_rate: string;
  max_daily_rate: string;
  daily_position_limit: number | null;
  active_position_count: string;
  active_principal: string;
  today_position_count: string;
  today_principal: string;
  settlement_position_count: string;
  settlement_principal: string;
  settlement_daily_rate: string | null;
  settlement_rate_status: number | null;
  settlement_rate_date: string | null;
};

type MarketDashboard = {
  summary: Record<string, string | number>;
  plans: Plan[];
  settlements: Record<string, string | number | null>;
  withdrawals: Record<string, string | number>;
  earnings: Record<string, string | number>;
  nodeFeePools: {
    pools: Array<Record<string, string | number | null>>;
    currentPeriodIncome: string;
    distributableAt30Percent: string;
    usdtBalance: string;
    latestSettlement: Record<string, string | number | null> | null;
  };
  risk: {
    ratePublication?: {
      settlementDate: string;
      launchDate: string | null;
      due: boolean;
      firstAutomaticPublicationAt: string | null;
    };
    currentRewardLiabilityUsdt: string;
    vaultBalance: { usdt: string; sage: string; iage: string };
    vaultDataComplete: boolean;
    vaultDataFresh: boolean;
    usdtSettlementEnabled: boolean;
    reserveGuardMode: "ENFORCE" | "ADVISORY";
    feeDividendRateOfStatic: string;
    payout: {
      theoreticalPayoutMultiplier: string;
      projections: Array<{
        horizonDays: 30 | 90 | 180;
        projectedRewardUsdt: string;
        totalEconomicLiabilityUsdt: string;
        totalLiabilityUsdt: string;
        sageLiability: string;
        iageLiability: string;
        usdtCoverageRatio: string | null;
        sageCoverageRatio: string | null;
        iageCoverageRatio: string | null;
        minimumCoverageRatio: string | null;
      }>;
      guard: {
        passed: boolean;
        minimumRequiredCoverageRatio: string;
        testedHorizonDays: number;
        reasons: string[];
      };
    };
    principalReserve: {
      dataAvailable: boolean;
      address: string | null;
      usdtBalance: string;
      reservedUsdt: string;
      availableUsdt: string;
      shortfallUsdt: string;
      usdtCoverageRatio: string | null;
      iageVaultBalance: string;
      reservedIagePrincipal: string;
      availableIageInventory: string;
      iagePrincipalCoverageRatio: string | null;
      treasurySageBalance: string;
      treasuryIageBalance: string;
      treasuryUsdtParticipationAllowance: string;
      treasuryIageParticipationAllowance: string;
    };
    consolidatedTreasury: {
      treasuryUsdtBalance: string;
      nodePrincipal: string;
      outstandingNodeReward: string;
      outstandingMarketReward: string;
      principalObligation: string;
      principalCoverageRatio: string | null;
      currentEconomicObligation: string;
      stressed180DayEconomicObligation: string;
      marketEarningBasisPrincipal: string;
    };
    nearCapCount: string | number;
    failedSettlementCount: string | number;
    failedWithdrawalCount: string | number;
  };
  payoutVaults: Array<Record<string, string | number | null>>;
  trend: Array<Record<string, string | number>>;
  launch: Record<string, string | number | null> | null;
  nodeSync: Record<string, string | number | null> | null;
  settlementReadiness: Array<Record<string, string | number | null>>;
  dynamicBatches: Array<Record<string, string | number | null>>;
  rankDistribution: Array<Record<string, string | number | null>>;
  assetReserve: Record<string, string | number | null> | null;
  assetMetrics: Record<string, string | number | null>;
  referralMetrics: Record<string, string | number | null>;
  chainIndex: Array<Record<string, string | number | null>>;
  allocationPolicies: Array<Record<string, string | number | null>>;
  simulatorPayout: Record<string, string | number | null>;
  principalMaturity?: {
    timeZone: "Asia/Shanghai";
    versions: PrincipalMaturityVersion[];
  };
  updatedAt: string;
};

type PrincipalMaturityBucketKey =
  | "OVERDUE" | "NEXT_24H" | "NEXT_7D" | "NEXT_30D" | "LATER";

type PrincipalMaturityVersion = {
  version: "V1" | "V2";
  label: string;
  configured: boolean;
  dataAvailable: boolean;
  participationAddress: string | null;
  reserveAddress: string | null;
  reserveUsdtBalance: string;
  onchainReservedUsdt: string;
  availableUsdt: string;
  positionCount: number;
  refundableUsdt: string;
  internalIage: string;
  dueThrough30DaysUsdt: string;
  topUpBy30DaysUsdt: string;
  fullFundingShortfallUsdt: string;
  reservationDeltaUsdt: string;
  reservationReconciled: boolean;
  buckets: Array<{
    key: PrincipalMaturityBucketKey;
    label: string;
    positionCount: number;
    usdtRequired: string;
    internalIage: string;
  }>;
};

type PrincipalMaturityPosition = {
  position_id: string;
  contract_position_id: string;
  contract_version: "V1" | "V2";
  user_address: string;
  identity_type: number;
  source_node_position_id: string | null;
  product_code: string;
  product_name: string;
  lock_days: number;
  market_usdt_amount: string;
  market_iage_amount: string;
  started_at: string;
  maturity_at: string;
  position_status: number;
  seconds_to_maturity: number;
};

type PrincipalMaturityList = {
  filters: {
    version: "ALL" | "V1" | "V2";
    bucket: "ALL" | PrincipalMaturityBucketKey;
  };
  rows: PrincipalMaturityPosition[];
  pagination: { page: number; pageSize: number; total: number; totalPages: number };
  updatedAt: string;
};

type PayoutFundingRow = {
  vaultId: number;
  payoutVaultAddress: string;
  settlementVaultAddress: string | null;
  usdtBalance: string;
  sageBalance: string;
  iageBalance: string;
  blockNumber: string | null;
  snapshotAt: string | null;
  pendingClaimCount: number;
  submittedClaimCount: number;
  pendingUsdtGross: string;
  confirmedUsdtGrossToday: string;
  lastConfirmedAt: string | null;
  immediateShortfallUsdt: string;
  recommendedReserveUsdt: string;
  recommendedTopUpUsdt: string;
  surplusAfterPendingUsdt: string;
  liquidityStatus: "MISSING_CONTRACT" | "TOP_UP_REQUIRED" | "READY";
};

type PayoutFlow = {
  flow_kind: "SAGE_USDT" | "IAGE_REINVEST";
  flow_id: string;
  user_address: string;
  reward_type: number;
  vault_id: number;
  payout_vault_address: string;
  settlement_vault_address: string | null;
  sage_amount: string;
  iage_amount: string;
  sage_quote_price: string;
  expected_usdt_gross: string;
  expected_usdt_fee: string;
  expected_usdt_net: string;
  actual_usdt_fee: string | null;
  actual_usdt_net: string | null;
  flow_status: number;
  tx_hash: string | null;
  block_number: string | null;
  prepared_at: string;
  submitted_at: string | null;
  confirmed_at: string | null;
};

type PayoutOperations = {
  summary: {
    pendingClaimCount: number;
    pendingUsdtGross: string;
    recommendedTopUpUsdt: string;
    pairsRequiringTopUp: number;
    reserveMultiplier: string;
    unassignedUsdtEquivalent: string;
    unassignedSage: string;
    unassignedIage: string;
  };
  fundingRows: PayoutFundingRow[];
  flows: PayoutFlow[];
  updatedAt: string;
};

type FlowFilter = "ALL" | "PENDING" | "CONFIRMED" | "ATTENTION";
type PrincipalVersionFilter = "ALL" | "V1" | "V2";
type PrincipalBucketFilter = "ALL" | PrincipalMaturityBucketKey;

const percent = (value?: string | number | null) => value == null
  ? "未设置"
  : `${(Number(value) * 100).toFixed(3)}%`;

const settlementStatus = (plan: Plan) => {
  if (plan.settlement_rate_status === 2) return { label: "已结算", tone: "success" as const };
  if (plan.settlement_rate_status === 1) return { label: "结算中", tone: "success" as const };
  if (plan.settlement_rate_status === 0) return { label: "草稿", tone: "warning" as const };
  return { label: "待设置", tone: "danger" as const };
};

const shanghaiDate = () => new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Shanghai",
  year: "numeric",
  month: "2-digit",
  day: "2-digit"
}).format(new Date());

const previousShanghaiDate = () => new Date(
  Date.parse(`${shanghaiDate()}T00:00:00.000Z`) - 24 * 60 * 60 * 1000
).toISOString().slice(0, 10);

const guardReasonLabel: Record<string, string> = {
  MARKET_FIRST_RATE_NOT_DUE: "尚未到首个发布日",
  PAYOUT_VAULT_SNAPSHOT_INCOMPLETE: "10 组收益/USDT 兑付 Vault 快照不完整",
  PAYOUT_VAULT_SNAPSHOT_STALE: "兑付 Vault 快照已过期",
  SAGE_COVERAGE_BELOW_MINIMUM: "SAGE 压力覆盖低于门槛",
  IAGE_COVERAGE_BELOW_MINIMUM: "IAGE 压力覆盖低于门槛",
  USDT_COVERAGE_BELOW_MINIMUM: "USDT 兑付储备覆盖低于门槛",
  REWARD_ALLOCATION_POLICY_MISSING: "缺少生效中的双币分配策略"
};

const compactAddress = (value: string | null | undefined) => value
  ? `${value.slice(0, 8)}…${value.slice(-6)}`
  : "—";

const shanghaiDateTime = (value: string) => new Intl.DateTimeFormat("zh-CN", {
  timeZone: "Asia/Shanghai",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false
}).format(new Date(value));

const maturityCountdown = (seconds: number) => {
  const overdue = seconds <= 0;
  let remaining = Math.abs(seconds);
  const days = Math.floor(remaining / 86_400);
  remaining -= days * 86_400;
  const hours = Math.floor(remaining / 3_600);
  remaining -= hours * 3_600;
  const minutes = Math.floor(remaining / 60);
  const compact = [days ? `${days} 天` : "", hours ? `${hours} 小时` : "", `${minutes} 分`]
    .filter(Boolean).join(" ");
  return overdue ? `已到期 ${compact}` : `${compact}后到期`;
};

const flowStatus = (status: number) => {
  if (status === 0) return { label: "已签发", tone: "warning" as const };
  if (status === 1) return { label: "链上确认中", tone: "info" as const };
  if (status === 2) return { label: "已完成", tone: "success" as const };
  if (status === 4) return { label: "链重组恢复", tone: "warning" as const };
  return { label: "已失效", tone: "danger" as const };
};

export function MarketOperationsPage() {
  const configuredChainId = Number(import.meta.env.VITE_CHAIN_ID ?? 56);
  const networkLabel = configuredChainId === 31337
    ? "BSC 本地模拟 · Chain 31337"
    : configuredChainId === 97
      ? "BSC Testnet · Chain 97"
      : "BSC Mainnet · Chain 56";
  const client = useQueryClient();
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [dailyRatePercent, setDailyRatePercent] = useState("");
  const [reason, setReason] = useState("");
  const [publish, setPublish] = useState(false);
  const [launchAt, setLaunchAt] = useState("");
  const [launchConfirmation, setLaunchConfirmation] = useState("");
  const [flowFilter, setFlowFilter] = useState<FlowFilter>("ALL");
  const [flowSearch, setFlowSearch] = useState("");
  const [payoutFlowPage, setPayoutFlowPage] = useState(1);
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);
  const [principalVersionFilter, setPrincipalVersionFilter] =
    useState<PrincipalVersionFilter>("ALL");
  const [principalBucketFilter, setPrincipalBucketFilter] =
    useState<PrincipalBucketFilter>("ALL");
  const [principalPage, setPrincipalPage] = useState(1);
  const dashboard = useQuery({
    queryKey: ["market-dashboard"],
    queryFn: () => adminApi<MarketDashboard>("/market/dashboard"),
    refetchInterval: 5000
  });
  const payoutOperations = useQuery({
    queryKey: ["market-payout-operations"],
    queryFn: () => adminApi<PayoutOperations>("/market/payout-operations"),
    refetchInterval: 5000
  });
  const principalMaturities = useQuery({
    queryKey: [
      "market-principal-maturities",
      principalVersionFilter,
      principalBucketFilter,
      principalPage
    ],
    queryFn: () => adminApi<PrincipalMaturityList>(
      `/market/principal-maturities?version=${principalVersionFilter}`
        + `&bucket=${principalBucketFilter}&page=${principalPage}&pageSize=${PRINCIPAL_PAGE_SIZE}`
    ),
    refetchInterval: 15_000
  });
  const rateMutation = useMutation({
    mutationFn: (payload: {
      productCode: Plan["product_code"];
      settlementDate: string;
      dailyRate: string;
      reason: string;
      publish: boolean;
    }) => adminApi("/market/rates", { method: "PATCH", body: JSON.stringify(payload) }),
    onSuccess: async () => {
      setEditingPlan(null);
      setReason("");
      setDailyRatePercent("");
      setPublish(false);
      await client.invalidateQueries({ queryKey: ["market-dashboard"] });
    }
  });
  const launchMutation = useMutation({
    mutationFn: () => adminApi("/market/launch", {
      method: "POST",
      body: JSON.stringify({
        launchAt: new Date(launchAt).toISOString(),
        confirmation: launchConfirmation
      })
    }),
    onSuccess: async () => {
      setLaunchConfirmation("");
      await client.invalidateQueries({ queryKey: ["market-dashboard"] });
    }
  });
  const maxTrend = useMemo(() => Math.max(
    1,
    ...(dashboard.data?.trend ?? []).map((row) => Number(row.inflow ?? 0))
  ), [dashboard.data?.trend]);
  const filteredPayoutFlows = useMemo(() => {
    const needle = flowSearch.trim().toLowerCase();
    return (payoutOperations.data?.flows ?? []).filter((flow) => {
      const status = Number(flow.flow_status);
      const matchesFilter = flowFilter === "ALL"
        || (flowFilter === "PENDING" && [0, 1].includes(status))
        || (flowFilter === "CONFIRMED" && status === 2)
        || (flowFilter === "ATTENTION" && [3, 4].includes(status));
      const matchesSearch = !needle || [
        flow.flow_id,
        flow.user_address,
        flow.tx_hash ?? "",
        flow.payout_vault_address,
        flow.settlement_vault_address ?? "",
        `pair ${flow.vault_id}`
      ].some((value) => value.toLowerCase().includes(needle));
      return matchesFilter && matchesSearch;
    });
  }, [flowFilter, flowSearch, payoutOperations.data?.flows]);
  const payoutFlowTotalPages = Math.max(
    1,
    Math.ceil(filteredPayoutFlows.length / PAYOUT_FLOW_PAGE_SIZE)
  );
  const payoutFlowCurrentPage = Math.min(payoutFlowPage, payoutFlowTotalPages);
  const paginatedPayoutFlows = filteredPayoutFlows.slice(
    (payoutFlowCurrentPage - 1) * PAYOUT_FLOW_PAGE_SIZE,
    payoutFlowCurrentPage * PAYOUT_FLOW_PAGE_SIZE
  );

  if (dashboard.isLoading) return <div className="admin-skeleton"><i /><i /><i /></div>;
  if (dashboard.isError || !dashboard.data) {
    return <div className="admin-error"><AlertTriangle /><h2>Market 监控数据获取失败</h2><button onClick={() => dashboard.refetch()}>重新请求</button></div>;
  }

  const data = dashboard.data;
  // Keep the page usable during a rolling deployment where the console may be
  // published a few minutes before the upgraded API starts returning this desk.
  const principalMaturityVersions = data.principalMaturity?.versions ?? [];
  const activeAllocationPolicy = data.allocationPolicies.find((policy) =>
    [1, 2].includes(Number(policy.policy_status))
  );
  const completed = Number(data.settlements.completed_count ?? 0);
  const expected = Number(data.settlements.expected_count ?? 0);
  const settlementProgress = expected ? Math.round(completed / expected * 100) : 100;
  const payoutProjection180 = data.risk.payout.projections.find((row) => row.horizonDays === 180);
  const coverage = payoutProjection180?.minimumCoverageRatio == null
    ? null : Number(payoutProjection180.minimumCoverageRatio);
  const requiredCoverage = Number(data.risk.payout.guard.minimumRequiredCoverageRatio);
  const reserveGuardAdvisory = data.risk.reserveGuardMode === "ADVISORY";
  const publicationDue = data.risk.ratePublication?.due ?? true;
  const ratePublicationAllowed = publicationDue
    && (data.risk.payout.guard.passed || reserveGuardAdvisory);
  // 收益提现只销毁 SAGE 并由配对 Vault 支付 USDT；IAGE 留在生态内复投。
  // 因此启用 USDT 结算后，SAGE 对应的 USDT 覆盖率必须参与发布闸门。
  const usdtCoverageBinding = data.risk.usdtSettlementEnabled;
  const payoutMultiplier = Number(data.risk.payout.theoreticalPayoutMultiplier);
  const feeDividendRate = Number(data.risk.feeDividendRateOfStatic ?? 0);
  const sageReferencePrice = Number(data.assetReserve?.sage_reference_price ?? 0);
  const coverageTone = !publicationDue
    ? "info"
    : !data.risk.payout.guard.passed
    ? "danger"
    : coverage == null || coverage >= requiredCoverage * 1.2
      ? "success" : "warning";

  const beginEdit = (plan: Plan) => {
    setEditingPlan(plan);
    setDailyRatePercent(plan.settlement_daily_rate
      ? (Number(plan.settlement_daily_rate) * 100).toFixed(3)
      : (Number(plan.min_daily_rate) * 100).toFixed(3));
    setReason("");
    setPublish(false);
  };

  const copyAddress = async (value: string) => {
    await navigator.clipboard.writeText(value);
    setCopiedAddress(value);
    window.setTimeout(() => setCopiedAddress((current) => current === value ? null : current), 1600);
  };

  const explorerOrigin = configuredChainId === 97
    ? "https://testnet.bscscan.com"
    : "https://bscscan.com";

  const saveRate = (event: FormEvent) => {
    event.preventDefault();
    if (!editingPlan) return;
    rateMutation.mutate({
      productCode: editingPlan.product_code,
      settlementDate: previousShanghaiDate(),
      dailyRate: (Number(dailyRatePercent) / 100).toFixed(8),
      reason,
      publish
    });
  };

  return (
    <div className="market-operations-page">
      <section className="market-command-bar">
        <div>
          <p className="eyebrow">MARKET COMMAND CENTER</p>
          <h2>资金、日结与兑付风险</h2>
          <span>Market 与节点账本独立统计 · {networkLabel}</span>
        </div>
        <div className="command-health">
          <StatusPill tone={coverageTone}>
            {!data.risk.vaultDataComplete
              ? "Vault 快照不完整"
              : !data.risk.vaultDataFresh
                ? "Vault 快照已过期"
                : coverage == null
                  ? "暂无奖励负债"
                  : `180 天奖励覆盖 ${coverage.toFixed(2)}×`}
          </StatusPill>
          <button type="button" aria-label="刷新 Market 数据" onClick={() => dashboard.refetch()}>
            <RefreshCcw size={16} />
          </button>
        </div>
      </section>

      <section className="market-kpi-ribbon" aria-label="Market 核心指标">
        <article className="market-kpi market-kpi--primary">
          <span>在投本金</span>
          <strong>{amount(data.summary.active_principal as string)} <small>USDT</small></strong>
          <p>累计参与 {data.summary.total_position_count ?? 0} 笔</p>
        </article>
        <article className="market-kpi"><ArrowDownToLine /><span>今日新增本金</span><strong>{amount(data.summary.today_inflow as string)}</strong><p>USDT</p></article>
        <article className="market-kpi"><UsersRound /><span>有效仓位</span><strong>{data.summary.active_position_count ?? 0}</strong><p>节点双身份 {data.summary.active_node_identity_count ?? 0}</p></article>
        <article className="market-kpi"><BadgeDollarSign /><span>180 天奖励覆盖</span><strong>{coverage == null ? "—" : `${coverage.toFixed(2)}×`}</strong><p>{!publicationDue ? "尚未到首个发布日" : `门槛 ${requiredCoverage.toFixed(2)}× · ${data.risk.payout.guard.passed ? "正常发布" : reserveGuardAdvisory ? "风险提示发布" : "禁止发布"}`}</p></article>
        <article className="market-kpi"><ShieldAlert /><span>接近三倍出局</span><strong>{data.risk.nearCapCount}</strong><p>达到收益上限 90%</p></article>
      </section>

      <section className="market-system-strip" aria-label="Market 上线与同步状态">
        <article>
          <Rocket />
          <div><span>Market 上线状态</span><strong>{data.launch ? "已激活" : "等待激活"}</strong><small>{data.launch?.launch_at ? new Date(String(data.launch.launch_at)).toLocaleString("zh-CN", { hour12: false }) : "上线时间决定存量节点快照边界"}</small></div>
          <StatusPill tone={data.launch ? "success" : "warning"}>{data.launch ? "不可变更" : "未启动"}</StatusPill>
        </article>
        <article>
          <Database />
          <div><span>节点身份同步</span><strong>{data.nodeSync?.synced_position_count ?? 0} 个仓位</strong><small>{data.nodeSync ? `推荐关系 ${data.nodeSync.synced_referral_count ?? 0} 条` : "激活后自动幂等同步"}</small></div>
          <StatusPill tone={Number(data.nodeSync?.sync_status ?? 0) === 2 ? "success" : data.nodeSync ? "warning" : "info"}>{["等待", "同步中", "已完成", "失败"][Number(data.nodeSync?.sync_status ?? 0)]}</StatusPill>
        </article>
        <article>
          <Clock3 />
          <div><span>近 7 日日结批次</span><strong>{data.settlementReadiness.length} 个</strong><small>未完成 {data.settlementReadiness.reduce((sum, row) => sum + Number(row.remaining_position_count ?? 0), 0)} 个仓位</small></div>
          <StatusPill tone={data.settlementReadiness.some((row) => Number(row.failed_position_count ?? 0) > 0) ? "danger" : "success"}>连续性监控</StatusPill>
        </article>
        <article>
          <Network />
          <div><span>动态奖励批次</span><strong>{data.dynamicBatches[0] ? `${data.dynamicBatches[0].credited_reward_amount ?? 0} USDT` : "等待结算"}</strong><small>{data.dynamicBatches[0] ? `级差 ${data.dynamicBatches[0].normal_reward_count ?? 0} 笔 · 平级 ${data.dynamicBatches[0].peer_reward_count ?? 0} 笔` : "V1–V7、小区与逐线奖励独立对账"}</small></div>
          <StatusPill tone={Number(data.dynamicBatches[0]?.batch_status ?? 0) === 2 ? "success" : data.dynamicBatches[0] ? "warning" : "info"}>{data.dynamicBatches[0] ? ["等待", "计算中", "已完成", "失败"][Number(data.dynamicBatches[0].batch_status ?? 0)] : "无批次"}</StatusPill>
        </article>
      </section>

      <section className="principal-maturity-console" aria-label="Market 本金到期与储备计划">
        <header>
          <div>
            <p className="eyebrow">PRINCIPAL MATURITY DESK</p>
            <h2>本金到期与储备计划</h2>
            <span>按上海时间自动追踪 V1 / V2 尚未退出仓位。USDT 备付只统计 Market 实际入金，不包含节点原始 1,000 USDT，也不把内部 IAGE 计入 USDT 缺口。</span>
          </div>
          <Landmark />
        </header>

        <div className="principal-reserve-lanes">
          {principalMaturityVersions.map((version) => {
            const topUpRequired = Number(version.topUpBy30DaysUsdt) > 0;
            const ledgerMismatch = version.dataAvailable && !version.reservationReconciled;
            const tone = !version.configured || ledgerMismatch
              ? "danger" as const
              : !version.dataAvailable || topUpRequired
                ? "warning" as const
                : "success" as const;
            const status = !version.configured
              ? "待部署配置"
              : !version.dataAvailable
                ? "链上读取失败"
                : ledgerMismatch
                  ? "账链待核对"
                  : topUpRequired
                    ? "30 天内需补资"
                    : "当前覆盖充足";
            return (
              <article className={`principal-reserve-lane principal-reserve-lane--${version.version.toLowerCase()}`} key={version.version}>
                <div className="principal-lane-heading">
                  <div><b>{version.version}</b><span>{version.label}</span><small>{version.positionCount} 个待退出仓位</small></div>
                  <StatusPill tone={tone}>{status}</StatusPill>
                </div>
                <div className="principal-reserve-addresses">
                  <span><small>参与合约</small><code title={version.participationAddress ?? undefined}>{compactAddress(version.participationAddress)}</code></span>
                  <i>→</i>
                  <span><small>本金储备合约</small><code title={version.reserveAddress ?? undefined}>{compactAddress(version.reserveAddress)}</code></span>
                  {version.reserveAddress && <div>
                    <button type="button" onClick={() => copyAddress(version.reserveAddress!)}><Copy size={13} />{copiedAddress === version.reserveAddress ? "已复制" : "复制储备地址"}</button>
                    <a href={`${explorerOrigin}/address/${version.reserveAddress}`} target="_blank" rel="noreferrer"><ExternalLink size={13} />BscScan</a>
                  </div>}
                </div>
                <div className="principal-reserve-metrics">
                  <div><span>储备现有余额</span><strong>{amount(version.reserveUsdtBalance)} <small>USDT</small></strong></div>
                  <div><span>账本待退本金</span><strong>{amount(version.refundableUsdt)} <small>USDT</small></strong></div>
                  <div><span>30 天内到期</span><strong>{amount(version.dueThrough30DaysUsdt)} <small>USDT</small></strong></div>
                  <div className={topUpRequired ? "is-attention" : ""}><span>30 天内建议补入</span><strong>{amount(version.topUpBy30DaysUsdt)} <small>USDT</small></strong></div>
                </div>
                <div className="principal-reconciliation">
                  <span>链上锁定 {amount(version.onchainReservedUsdt)} USDT</span>
                  <span>全部仓位资金缺口 {amount(version.fullFundingShortfallUsdt)} USDT</span>
                  <span>内部 IAGE 本金 {amount(version.internalIage)} IAGE</span>
                  <strong className={ledgerMismatch ? "is-risk" : ""}>{!version.configured
                    ? "配置 V2 地址后自动接入"
                    : !version.dataAvailable
                      ? "暂不可核对账链"
                      : version.reservationReconciled
                        ? "账本与链上预约一致"
                        : `账链差额 ${amount(version.reservationDeltaUsdt)} USDT`}</strong>
                </div>
                <div className="principal-maturity-buckets">
                  {version.buckets.map((bucket) => (
                    <button type="button" key={bucket.key} className={
                      principalVersionFilter === version.version
                        && principalBucketFilter === bucket.key ? "is-active" : ""
                    } onClick={() => {
                      setPrincipalVersionFilter(version.version);
                      setPrincipalBucketFilter(bucket.key);
                      setPrincipalPage(1);
                    }}>
                      <span>{bucket.label}</span>
                      <strong>{amount(bucket.usdtRequired)} <small>USDT</small></strong>
                      <em>{bucket.positionCount} 笔</em>
                    </button>
                  ))}
                </div>
              </article>
            );
          })}
          {!principalMaturityVersions.length && (
            <div className="principal-reserve-empty">
              <Landmark />
              <div><strong>本金储备数据等待接入</strong><span>后端升级或配置 V2 合约地址后，这里会自动显示 V1 / V2 两条储备线。</span></div>
            </div>
          )}
        </div>

        <div className="principal-position-ledger">
          <div className="principal-position-toolbar">
            <div>
              <strong>逐仓到期清单</strong>
              <span>显示每个仓位应由哪个版本的本金储备兑付</span>
            </div>
            <div className="principal-filter-groups">
              <div role="tablist" aria-label="合约版本">
                {(["ALL", "V1", "V2"] as PrincipalVersionFilter[]).map((version) => (
                  <button type="button" role="tab" aria-selected={principalVersionFilter === version} className={principalVersionFilter === version ? "is-active" : ""} key={version} onClick={() => {
                    setPrincipalVersionFilter(version);
                    setPrincipalPage(1);
                  }}>{version === "ALL" ? "全部版本" : version}</button>
                ))}
              </div>
              <div role="tablist" aria-label="到期区间">
                {([
                  ["ALL", "全部到期"], ["OVERDUE", "已到期"], ["NEXT_24H", "24 小时"],
                  ["NEXT_7D", "2–7 天"], ["NEXT_30D", "8–30 天"], ["LATER", "30 天后"]
                ] as Array<[PrincipalBucketFilter, string]>).map(([bucket, label]) => (
                  <button type="button" role="tab" aria-selected={principalBucketFilter === bucket} className={principalBucketFilter === bucket ? "is-active" : ""} key={bucket} onClick={() => {
                    setPrincipalBucketFilter(bucket);
                    setPrincipalPage(1);
                  }}>{label}</button>
                ))}
              </div>
            </div>
          </div>

          {principalMaturities.isLoading ? (
            <div className="principal-position-empty"><RefreshCcw />正在读取到期仓位…</div>
          ) : principalMaturities.isError || !principalMaturities.data ? (
            <div className="principal-position-empty is-error"><AlertTriangle />本金到期清单读取失败<button type="button" onClick={() => principalMaturities.refetch()}>重新请求</button></div>
          ) : principalMaturities.data.rows.length ? <>
            <div className="principal-position-table">
              <div className="principal-position-table-head"><span>版本 / 仓位</span><span>用户 / 身份</span><span>周期</span><span>Market 本金</span><span>到期时间（上海）</span><span>兑付状态</span></div>
              {principalMaturities.data.rows.map((position) => {
                const version = principalMaturityVersions.find((item) => item.version === position.contract_version);
                const matured = position.seconds_to_maturity <= 0;
                return <article key={position.position_id}>
                  <div><StatusPill tone={position.contract_version === "V2" ? "info" : "warning"}>{position.contract_version}</StatusPill><code>链上 #{position.contract_position_id}</code><small>账本 #{position.position_id}</small></div>
                  <div><code title={position.user_address}>{compactAddress(position.user_address)}</code><span>{position.identity_type === 2 ? "节点双身份" : "普通 Market"}</span><button type="button" onClick={() => copyAddress(position.user_address)}><Copy size={12} />{copiedAddress === position.user_address ? "已复制" : "复制"}</button></div>
                  <div><strong>{position.lock_days}</strong><span>{position.product_name}</span><small>{shanghaiDateTime(position.started_at)} 入金</small></div>
                  <div><strong>{amount(position.market_usdt_amount)} USDT</strong><span>内部 IAGE {amount(position.market_iage_amount)}</span><small>节点原始本金不计入</small></div>
                  <div><strong>{shanghaiDateTime(position.maturity_at)}</strong><span className={matured ? "is-risk" : ""}>{maturityCountdown(position.seconds_to_maturity)}</span></div>
                  <div><StatusPill tone={!version?.configured ? "danger" : matured ? "warning" : "success"}>{!version?.configured ? "储备未配置" : matured ? "可准备退出" : "尚在锁定期"}</StatusPill><small>{compactAddress(version?.reserveAddress)}</small></div>
                </article>;
              })}
            </div>
            <PaginationBar
              page={principalMaturities.data.pagination.page}
              pageSize={PRINCIPAL_PAGE_SIZE}
              total={principalMaturities.data.pagination.total}
              itemLabel="个仓位"
              onPageChange={setPrincipalPage}
            />
          </> : <div className="principal-position-empty"><CheckCircle2 />当前筛选区间没有待退出的 Market 本金。</div>}
        </div>
      </section>

      <section className="plan-control daily-rate-control" aria-label="昨日收益率与 24 小时参与数据设置">
        <header>
          <div className="daily-rate-heading">
            <p className="eyebrow">DAILY RATE CONTROL</p>
            <h2>昨日收益率与 24 小时参与数据</h2>
            <span>上线默认使用各周期最低收益率；后续可在允许区间内按日调整，发布后立即进入日结且不可修改。</span>
          </div>
          <div className="daily-rate-cutoff">
            <Clock3 size={18} />
            <span>待结算日期</span>
            <strong>{previousShanghaiDate()}</strong>
          </div>
        </header>
        <div className="plan-table">
          {data.plans.map((plan) => {
            const status = settlementStatus(plan);
            return (
              <article key={plan.product_code}>
                <div className="plan-identity"><b>{plan.lock_days}</b><span>天周期</span></div>
                <div className="plan-participation"><span>昨日参与</span><strong>{plan.settlement_position_count} 人次</strong><small>{amount(plan.settlement_principal)} USDT · 今日已用 {plan.today_position_count}{plan.daily_position_limit ? ` / ${plan.daily_position_limit}` : ""}</small></div>
                <div><span>在投本金</span><strong>{amount(plan.active_principal)}</strong><small>{plan.active_position_count} 个有效仓位</small></div>
                <div className="plan-rate"><span>{plan.settlement_daily_rate == null ? "上线默认收益率" : "昨日收益率"}</span><strong>{percent(plan.settlement_daily_rate ?? plan.min_daily_rate)}</strong><small>允许区间 {percent(plan.min_daily_rate)}–{percent(plan.max_daily_rate)}</small></div>
                <div className="plan-actions">
                  <StatusPill tone={status.tone}>{status.label}</StatusPill>
                  <SecondaryButton type="button" disabled={[1, 2].includes(Number(plan.settlement_rate_status))} onClick={() => beginEdit(plan)}>{[1, 2].includes(Number(plan.settlement_rate_status)) ? "已确定" : "设置收益率"}</SecondaryButton>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {editingPlan && (
        <section className="rate-editor daily-rate-editor" aria-labelledby="rate-editor-title">
          <header><div><p className="eyebrow">AUDITED CHANGE</p><h2 id="rate-editor-title">设置 {previousShanghaiDate()} · {editingPlan.lock_days} 天周期收益率</h2></div><button type="button" onClick={() => setEditingPlan(null)}>关闭</button></header>
          <form onSubmit={saveRate}>
            <label>昨日收益率（%）<input type="number" required step="0.001" min={Number(editingPlan.min_daily_rate) * 100} max={Number(editingPlan.max_daily_rate) * 100} value={dailyRatePercent} onChange={(event) => setDailyRatePercent(event.target.value)} /></label>
            <label className="rate-reason">调整依据<textarea required minLength={8} maxLength={500} placeholder="填写参与人数、资金体量、兑付覆盖或风控判断，至少 8 个字符" value={reason} onChange={(event) => setReason(event.target.value)} /></label>
            <label className={`publish-check${!data.risk.payout.guard.passed && reserveGuardAdvisory ? " is-advisory" : ""}`}><input type="checkbox" disabled={!ratePublicationAllowed} checked={publish} onChange={(event) => setPublish(event.target.checked)} /><span><strong>发布并结算</strong>{data.risk.payout.guard.passed ? "发布后立即进入昨日 24 小时日结，不可再修改；未勾选仅保存草稿。" : reserveGuardAdvisory ? "当前储备不足；发布将记录风险覆盖状态，但仍允许进入日结。" : "兑付储备闸门未通过；当前只允许保存草稿。"}</span></label>
            <PrimaryButton type="submit" disabled={rateMutation.isPending || (publish && !ratePublicationAllowed)}>{rateMutation.isPending ? "保存中…" : publish ? "保存并发布" : "保存草稿"}</PrimaryButton>
            {rateMutation.isError && <p className="inline-error" role="alert">{rateMutation.error.message}</p>}
          </form>
        </section>
      )}

      {!data.launch && (
        <section className="launch-control">
          <header><LockKeyhole /><div><p className="eyebrow">IRREVERSIBLE ACTIVATION</p><h2>激活 Market 上线时间</h2><span>激活后以该时刻快照存量节点和推荐关系，并自动创建 180 天 Market 身份。上线时间不可修改。</span></div></header>
          <div className="launch-fields">
            <label>上线时间<input type="datetime-local" value={launchAt} onChange={(event) => setLaunchAt(event.target.value)} /></label>
            <label>确认短语<input value={launchConfirmation} placeholder="输入 ACTIVATE_MARKET" onChange={(event) => setLaunchConfirmation(event.target.value)} /></label>
            <PrimaryButton type="button" disabled={!launchAt || launchConfirmation !== "ACTIVATE_MARKET" || launchMutation.isPending} onClick={() => launchMutation.mutate()}>{launchMutation.isPending ? "激活中…" : "确认激活"}</PrimaryButton>
          </div>
          {launchMutation.isError && <p className="inline-error" role="alert">{launchMutation.error.message}</p>}
        </section>
      )}

      <section className="market-monitor-grid">
        <article className="settlement-monitor">
          <header>
            <div><p className="eyebrow">D+1 SETTLEMENT</p><h2>昨日 24 小时结算进度</h2></div>
            <StatusPill tone={Number(data.settlements.failed_count ?? 0) ? "danger" : "success"}>
              {Number(data.settlements.failed_count ?? 0) ? `${data.settlements.failed_count} 笔失败` : "队列正常"}
            </StatusPill>
          </header>
          <div className="settlement-dial" style={{ "--progress": `${settlementProgress}%` } as CSSProperties}>
            <strong>{settlementProgress}%</strong><span>{completed} / {expected} 笔</span>
          </div>
          <dl>
            <div><dt>静态收益</dt><dd>{amount(data.settlements.static_earning as string)}</dd></div>
            <div><dt>直推 10%</dt><dd>{amount(data.settlements.direct_earning as string)}</dd></div>
            <div><dt>团队收益</dt><dd>{amount(data.settlements.team_earning as string)}</dd></div>
          </dl>
        </article>

        <article className="risk-ledger">
          <header><div><p className="eyebrow">RISK LEDGER</p><h2>兑付与异常</h2></div><CircleGauge /></header>
          <div><span>当前奖励负债</span><strong>{amount(data.risk.currentRewardLiabilityUsdt)} USDT 计价</strong></div>
          <div className={payoutProjection180?.sageCoverageRatio != null && Number(payoutProjection180.sageCoverageRatio) < requiredCoverage ? "is-risk" : ""}><span>SAGE 180 天覆盖</span><strong>{payoutProjection180?.sageCoverageRatio == null ? "暂无负债" : `${Number(payoutProjection180.sageCoverageRatio).toFixed(2)}×`}</strong></div>
          <div className={payoutProjection180?.iageCoverageRatio != null && Number(payoutProjection180.iageCoverageRatio) < requiredCoverage ? "is-risk" : ""}><span>IAGE 180 天覆盖</span><strong>{payoutProjection180?.iageCoverageRatio == null ? "暂无负债" : `${Number(payoutProjection180.iageCoverageRatio).toFixed(2)}×`}</strong></div>
          <div className={usdtCoverageBinding && payoutProjection180?.usdtCoverageRatio != null && Number(payoutProjection180.usdtCoverageRatio) < requiredCoverage ? "is-risk" : ""}><span>USDT 兑付 180 天覆盖{usdtCoverageBinding ? "" : "（未启用 · 仅展示）"}</span><strong>{payoutProjection180?.usdtCoverageRatio == null ? "暂无负债" : `${Number(payoutProjection180.usdtCoverageRatio).toFixed(2)}×`}</strong></div>
          <div><span>提现排队</span><strong>{data.withdrawals.pending_count ?? 0} 笔 · {amount(data.withdrawals.pending_net_amount as string)} USDT</strong></div>
          <div><span>收益领取待确认</span><strong>{data.earnings.pending_claim_count ?? 0} 笔</strong></div>
          <div><span>双币累计分配</span><strong>{amount(data.earnings.sage_allocated as string)} SAGE · {amount(data.earnings.iage_allocated as string)} IAGE</strong></div>
          <div><span>SAGE 已销毁 / IAGE 已复投</span><strong>{amount(data.earnings.sage_claimed_net as string)} SAGE · {amount(data.earnings.iage_reinvested_net as string)} IAGE</strong></div>
          <div><span>已收手续费</span><strong>{amount(data.withdrawals.confirmed_fee_amount as string)} USDT</strong></div>
          <div><span>违约管理费</span><strong>{amount(data.withdrawals.confirmed_penalty_amount as string)} USDT</strong></div>
          <div className={Number(data.risk.failedWithdrawalCount) ? "is-risk" : ""}><span>提现失败</span><strong>{data.risk.failedWithdrawalCount} 笔</strong></div>
          <div className={Number(data.risk.failedSettlementCount) ? "is-risk" : ""}><span>日结失败</span><strong>{data.risk.failedSettlementCount} 笔</strong></div>
        </article>
        <article className="risk-ledger">
          <header><div><p className="eyebrow">RANK EXPOSURE</p><h2>等级与动态拨出</h2></div><Network /></header>
          <div><span>已定级账户</span><strong>{data.rankDistribution.reduce((sum, row) => sum + (Number(row.rank_level) > 0 ? Number(row.account_count) : 0), 0)} 个</strong></div>
          <div><span>计算模型</span><strong>{data.dynamicBatches[0]?.calculation_model ?? "等待 V3 批次"}</strong></div>
          <div><span>当前最高等级</span><strong>V{Math.max(0, ...data.rankDistribution.map((row) => Number(row.rank_level)))}</strong></div>
          <div><span>正常级差</span><strong>{data.dynamicBatches[0]?.normal_reward_count ?? 0} 笔 · {amount(data.dynamicBatches[0]?.credited_reward_amount as string)} USDT 总动态</strong></div>
          <div><span>平级 / 被超越</span><strong>{data.dynamicBatches[0]?.peer_reward_count ?? 0} 笔</strong></div>
          <div><span>理论拨出上界</span><strong>静态基数的 {(payoutMultiplier * 100).toFixed(1)}%（198% + 1.1 × 分红率）</strong></div>
        </article>
      </section>

      <section className="plan-control payout-risk-control" aria-label="未来奖励负债、节点 Treasury 与 Market 独立本金储备风险">
        <header>
          <div>
            <p className="eyebrow">PAYOUT STRESS CONTROL</p>
            <h2>30 / 90 / 180 天奖励负债与储备覆盖</h2>
            <span>按各周期最高静态率、V3 理论收益拨比 {payoutMultiplier.toFixed(2)}×（198% + 1.1 × 分红率 {(feeDividendRate * 100).toFixed(2)}%）和仓位剩余三倍上限压力计算；SAGE 与 IAGE 库存分别达标，USDT 只覆盖可提现的 SAGE 95% 价值。当前模式：{reserveGuardAdvisory ? "风险提示，可人工或自动发布" : usdtCoverageBinding ? "强制拦截" : "仅展示"}。</span>
          </div>
          <StatusPill tone={!publicationDue ? "info" : data.risk.payout.guard.passed ? "success" : reserveGuardAdvisory ? "warning" : "danger"}>
            {!publicationDue ? "尚未到首个发布日" : data.risk.payout.guard.passed ? "收益率发布闸门已通过" : reserveGuardAdvisory ? "储备不足 · 允许风险发布" : "收益率发布已锁定"}
          </StatusPill>
        </header>
        <div className="payout-risk-projections">
          {data.risk.payout.projections.map((projection) => (
            <article key={projection.horizonDays}>
              <span>{projection.horizonDays} 天压力口径</span>
              <strong>{amount(projection.projectedRewardUsdt)} <small>USDT 计价</small></strong>
              <dl>
                <div><dt>总经济负债</dt><dd>{amount(projection.totalEconomicLiabilityUsdt)}</dd></div>
                <div><dt>USDT 可提现负债</dt><dd>{amount(projection.totalLiabilityUsdt)}</dd></div>
                <div><dt>SAGE 负债</dt><dd>{amount(projection.sageLiability)}</dd></div>
                <div><dt>IAGE 负债</dt><dd>{amount(projection.iageLiability)}</dd></div>
                <div><dt>最低覆盖</dt><dd className={projection.minimumCoverageRatio != null && Number(projection.minimumCoverageRatio) < requiredCoverage ? "is-risk" : ""}>{projection.minimumCoverageRatio == null ? "暂无负债" : `${Number(projection.minimumCoverageRatio).toFixed(2)}×`}</dd></div>
              </dl>
            </article>
          ))}
          <article className="payout-risk-treasury">
            <span>Treasury 与 Market 分币种储备</span>
            <strong>{data.risk.principalReserve.dataAvailable
              ? `${amount(data.risk.principalReserve.reservedUsdt)} USDT`
              : "链上数据不可用"} <small>Market 本金已锁定</small></strong>
            <dl>
              <div><dt>Market 本金退出储备</dt><dd>{amount(data.risk.principalReserve.usdtBalance)} USDT</dd></div>
              <div><dt>退出流动性缺口</dt><dd>{amount(data.risk.principalReserve.shortfallUsdt)} USDT</dd></div>
              <div><dt>超额可调回余额</dt><dd>{amount(data.risk.principalReserve.availableUsdt)} USDT</dd></div>
              <div><dt>10 Vault IAGE / 已锁本金</dt><dd>{amount(data.risk.principalReserve.iageVaultBalance)} / {amount(data.risk.principalReserve.reservedIagePrincipal)} IAGE</dd></div>
              <div><dt>Vault 可用 IAGE 库存</dt><dd>{amount(data.risk.principalReserve.availableIageInventory)} IAGE</dd></div>
              <div><dt>IAGE 本金覆盖</dt><dd>{data.risk.principalReserve.iagePrincipalCoverageRatio == null ? "暂无 IAGE 本金" : `${Number(data.risk.principalReserve.iagePrincipalCoverageRatio).toFixed(2)}×`}</dd></div>
              <div><dt>Treasury SAGE / IAGE</dt><dd>{amount(data.risk.principalReserve.treasurySageBalance)} / {amount(data.risk.principalReserve.treasuryIageBalance)}</dd></div>
              <div><dt>节点本金（共享 Treasury）</dt><dd>{amount(data.risk.consolidatedTreasury.nodePrincipal)} USDT</dd></div>
              <div><dt>节点本金覆盖</dt><dd>{data.risk.consolidatedTreasury.principalCoverageRatio == null ? "暂无本金义务" : `${Number(data.risk.consolidatedTreasury.principalCoverageRatio).toFixed(2)}×`}</dd></div>
              <div><dt>Treasury → Market 参与合约额度</dt><dd>{amount(data.risk.principalReserve.treasuryUsdtParticipationAllowance)} USDT · {amount(data.risk.principalReserve.treasuryIageParticipationAllowance)} IAGE</dd></div>
            </dl>
          </article>
        </div>
        {!publicationDue ? (
          <p className="payout-risk-lock is-pending"><Clock3 size={16} />
            {data.risk.ratePublication?.firstAutomaticPublicationAt
              ? `${new Date(data.risk.ratePublication.firstAutomaticPublicationAt).toLocaleString("zh-CN", { hour12: false })} 自动发布上线日最低收益率与 95% SAGE / 5% IAGE 分配策略`
              : "等待 Market 激活"}
          </p>
        ) : !data.risk.payout.guard.passed && (
          <p className="payout-risk-lock"><ShieldAlert size={16} />
            {data.risk.payout.guard.reasons.map((reason) => guardReasonLabel[reason] ?? reason).join("；")}
          </p>
        )}
      </section>

      <section className="market-asset-monitor" aria-label="Market 资产与推广监控">
        <article className="risk-ledger">
          <header><div><p className="eyebrow">ASSET RESERVE</p><h2>兑换储备</h2></div><ArrowLeftRight /></header>
          <div><span>USDT 储备</span><strong>{amount(data.assetReserve?.usdt_balance as string)} USDT</strong></div>
          <div><span>SAGE 储备</span><strong>{amount(data.assetReserve?.sage_balance as string)} SAGE</strong></div>
          <div><span>IAGE 储备</span><strong>{amount(data.assetReserve?.iage_balance as string)} IAGE</strong></div>
          <div><span>IAGE 用途</span><strong>生态参与 / 复投</strong></div>
          <div><span>SAGE 价格中枢</span><strong>{data.assetReserve?.sage_reference_price ?? "未同步"} USDT</strong></div>
        </article>
        <article className="risk-ledger">
          <header><div><p className="eyebrow">ASSET FLOW</p><h2>闪兑与 IAGE 流量</h2></div><BadgeDollarSign /></header>
          <div><span>SAGE 买入</span><strong>{data.assetMetrics.sage_buy_count ?? 0} 笔 · {amount(data.assetMetrics.sage_buy_usdt_volume as string)} USDT</strong></div>
          <div><span>SAGE 卖出</span><strong>{data.assetMetrics.sage_sell_count ?? 0} 笔 · {amount(data.assetMetrics.sage_sell_usdt_volume as string)} USDT</strong></div>
          <div><span>IAGE 生态流入</span><strong>{data.assetMetrics.iage_buy_count ?? 0} 笔 · {amount(data.assetMetrics.iage_issued as string)} IAGE</strong></div>
          <div><span>IAGE 生态净流通</span><strong>{amount(data.assetMetrics.net_iage_liability as string)} IAGE</strong></div>
          <div><span>IAGE 兑回状态</span><strong>永久禁用</strong></div>
        </article>
        <article className="risk-ledger">
          <header><div><p className="eyebrow">COMBINED NODE DIVIDEND</p><h2>节点 + Market 分红池</h2></div><BadgeDollarSign /></header>
          {data.nodeFeePools.pools.map((pool) => (
            <div key={String(pool.contract_address)}>
              <span>{Number(pool.contract_type) === 2 ? "原节点分红池" : "Market 分红池"}</span>
              <strong>{amount(pool.usdt_balance as string)} USDT</strong>
            </div>
          ))}
          <div><span>本周合并手续费</span><strong>{amount(data.nodeFeePools.currentPeriodIncome)} USDT</strong></div>
          <div><span>本周可分红（30%）</span><strong>{amount(data.nodeFeePools.distributableAt30Percent)} USDT</strong></div>
          <div className={data.nodeFeePools.pools.length === 2 ? "" : "is-risk"}>
            <span>双池绑定状态</span>
            <strong>{data.nodeFeePools.pools.length === 2 ? "已绑定 · 同节点快照" : "配置不完整"}</strong>
          </div>
        </article>
        <article className="risk-ledger">
          <header><div><p className="eyebrow">GROWTH INTEGRITY</p><h2>邀请与索引覆盖</h2></div><UsersRound /></header>
          <div><span>有效 Market 邀请码</span><strong>{data.referralMetrics.active_code_count ?? 0} 个</strong></div>
          <div><span>已绑定关系</span><strong>{data.referralMetrics.relation_count ?? 0} 条</strong></div>
          <div><span>自然参与账户</span><strong>{data.referralMetrics.organic_participant_count ?? 0} / {data.referralMetrics.participant_count ?? 0}</strong></div>
          <div><span>单账户最大直推</span><strong>{data.referralMetrics.max_direct_count ?? 0} 个</strong></div>
          <div><span>链上事件入账</span><strong>{data.chainIndex.reduce((sum, row) => sum + Number(row.event_count ?? 0), 0)} 条 · {data.chainIndex.length} 类</strong></div>
        </article>
      </section>

      <section className="market-monitor-grid" aria-label="模拟器 V3 拨出监控">
        <article className="risk-ledger">
          <header><div><p className="eyebrow">SIMULATOR V3 · PROMOTION</p><h2>昨日市场推广拨出</h2></div><CircleGauge /></header>
          <div><span>静态收益本体</span><strong>{amount(data.simulatorPayout.static_amount as string)} USDT · 100%</strong></div>
          <div><span>直推 10%</span><strong>{amount(data.simulatorPayout.direct_amount as string)} USDT</strong></div>
          <div><span>正常逐线级差</span><strong>{amount(data.simulatorPayout.normal_team_amount as string)} USDT</strong></div>
          <div><span>平级 / 被超越</span><strong>{amount(data.simulatorPayout.peer_amount as string)} USDT</strong></div>
          <div><span>手续费分红</span><strong>{amount(data.simulatorPayout.fee_dividend_amount as string)} USDT</strong></div>
        </article>
        <article className="risk-ledger">
          <header><div><p className="eyebrow">PAYOUT CEILING</p><h2>实际拨出与天花板</h2></div><ShieldAlert /></header>
          <div><span>全口径拨出</span><strong>{amount(data.simulatorPayout.total_payout as string)} USDT</strong></div>
          <div><span>实际拨出率</span><strong>{data.simulatorPayout.actual_payout_rate == null ? "暂无静态基数" : `${Number(data.simulatorPayout.actual_payout_rate).toFixed(2)}% × 静态`}</strong></div>
          <div><span>理论天花板</span><strong>{Number(data.simulatorPayout.theoretical_ceiling_rate ?? 198).toFixed(2)}% × 静态</strong></div>
          <div><span>天花板公式</span><strong>198% + 1.1 × 分红率 f</strong></div>
          <div className={Number(data.simulatorPayout.actual_payout_rate ?? 0) > Number(data.simulatorPayout.theoretical_ceiling_rate ?? 198) ? "is-risk" : ""}><span>模型校验</span><strong>{Number(data.simulatorPayout.actual_payout_rate ?? 0) > Number(data.simulatorPayout.theoretical_ceiling_rate ?? 198) ? "超出 V3 边界" : "边界内"}</strong></div>
        </article>
      </section>

      <section className="plan-control reward-allocation-control">
        <header>
          <div><p className="eyebrow">DUAL TOKEN ALLOCATION</p><h2>SAGE + IAGE 收益分配策略</h2></div>
          <span>按 USDT 价值固定拆分；发布昨日收益率时自动锁定当前 SAGE 价格。</span>
        </header>
        <div className="allocation-policy-summary">
          <article>
            <span>固定 USDT 价值比例</span>
            <strong>SAGE 95% · IAGE 5%</strong>
            <small>每 100 USDT 收益：95 USDT 价值分配为 SAGE，5 USDT 价值分配为 IAGE</small>
          </article>
          <article>
            <span>当前 SAGE/USDT 参考价</span>
            <strong>{sageReferencePrice > 0 ? `${sageReferencePrice.toFixed(4)} USDT` : "等待链上价格"}</strong>
            <small>{activeAllocationPolicy
              ? `最近锁定：${String(activeAllocationPolicy.effective_date).slice(0, 10)} · ${Number(activeAllocationPolicy.sage_settlement_price).toFixed(4)} USDT`
              : "首次发布昨日收益率时自动生成分配快照"}</small>
          </article>
        </div>
      </section>

      <section className="market-trend">
        <header><div><p className="eyebrow">7 DAY FLOW</p><h2>近 7 日资金与收益</h2></div><span>柱高代表每日新增本金</span></header>
        <div className="trend-bars">
          {data.trend.length ? data.trend.map((row) => (
            <div key={String(row.day_key)}>
              <span className="trend-value">{amount(row.inflow)}</span>
              <i style={{ height: `${Math.max(5, Number(row.inflow) / maxTrend * 100)}%` }} />
              <b>{new Date(String(row.day_key)).toLocaleDateString("zh-CN", { month: "numeric", day: "numeric" })}</b>
              <small>收益 {amount(Number(row.static_earning) + Number(row.dynamic_earning))}</small>
            </div>
          )) : <p className="market-empty">上线后将在这里显示资金与收益趋势。</p>}
        </div>
      </section>

      <section className="payout-funding-console">
        <header>
          <div>
            <p className="eyebrow">PAYOUT LIQUIDITY DESK</p>
            <h2>10 组兑付资金调拨台</h2>
            <span>按已绑定到各组的待兑付 Claim 计算。建议补入额含 {Number(payoutOperations.data?.summary.reserveMultiplier ?? requiredCoverage).toFixed(2)}× 缓冲。</span>
          </div>
          <Landmark />
        </header>

        {payoutOperations.isLoading ? (
          <div className="payout-ops-loading"><RefreshCcw /><span>正在核对 Vault 余额与待兑付流水…</span></div>
        ) : payoutOperations.isError || !payoutOperations.data ? (
          <div className="payout-ops-error"><AlertTriangle /><span>兑付资金数据获取失败</span><button type="button" onClick={() => payoutOperations.refetch()}>重新请求</button></div>
        ) : (
          <>
            <div className="payout-funding-summary">
              <article><span>已绑定待兑付</span><strong>{amount(payoutOperations.data.summary.pendingUsdtGross)} <small>USDT</small></strong><p>{payoutOperations.data.summary.pendingClaimCount} 笔 Claim</p></article>
              <article className={payoutOperations.data.summary.pairsRequiringTopUp ? "is-attention" : ""}><span>建议合计补入</span><strong>{amount(payoutOperations.data.summary.recommendedTopUpUsdt)} <small>USDT</small></strong><p>{payoutOperations.data.summary.pairsRequiringTopUp} 组需要处理</p></article>
              <article><span>尚未绑定 Vault 的收益</span><strong>{amount(payoutOperations.data.summary.unassignedUsdtEquivalent)} <small>USDT 计价</small></strong><p>{amount(payoutOperations.data.summary.unassignedSage)} SAGE · {amount(payoutOperations.data.summary.unassignedIage)} IAGE</p></article>
            </div>

            <div className="payout-funding-rule"><ShieldAlert size={17} /><span><strong>调拨规则</strong> 仅由 Treasury 多签向右侧“USDT 兑付合约”转入 USDT；不要把 USDT 转入左侧收益 Vault。用户发起提现时，系统会在实时余额、SAGE 库存及链上额度均充足的 Pair 中随机选择并立即预留；尚未领取的收益在生成 Claim 前不属于任何一组。</span></div>

            <div className="payout-funding-head" aria-hidden="true">
              <span>配对</span><span>收益库存</span><span>USDT 资金状态</span><span>目标兑付合约</span><span>操作判断</span>
            </div>
            <div className="payout-funding-list">
              {payoutOperations.data.fundingRows.map((row) => {
                const requiresTopUp = row.liquidityStatus === "TOP_UP_REQUIRED";
                const missingContract = row.liquidityStatus === "MISSING_CONTRACT";
                return (
                  <article className={`payout-funding-row${requiresTopUp || missingContract ? " is-attention" : ""}`} key={row.vaultId}>
                    <div className="payout-pair-id"><b>{String(row.vaultId).padStart(2, "0")}</b><span>Vault Pair</span><small>区块 {row.blockNumber ?? "—"}</small></div>
                    <div className="payout-inventory">
                      <span><small>SAGE</small><strong>{amount(row.sageBalance)}</strong></span>
                      <span><small>IAGE</small><strong>{amount(row.iageBalance)}</strong></span>
                      <code title={row.payoutVaultAddress}>{compactAddress(row.payoutVaultAddress)}</code>
                    </div>
                    <dl className="payout-liquidity-metrics">
                      <div><dt>当前余额</dt><dd>{amount(row.usdtBalance)} USDT</dd></div>
                      <div><dt>待兑付</dt><dd>{amount(row.pendingUsdtGross)} USDT · {row.pendingClaimCount} 笔</dd></div>
                      <div><dt>缓冲目标</dt><dd>{amount(row.recommendedReserveUsdt)} USDT</dd></div>
                      <div><dt>建议补入</dt><dd className={requiresTopUp ? "is-risk" : ""}>{amount(row.recommendedTopUpUsdt)} USDT</dd></div>
                    </dl>
                    <div className="payout-target-address">
                      <span>USDT 兑付合约</span>
                      {row.settlementVaultAddress ? <>
                        <code title={row.settlementVaultAddress}>{row.settlementVaultAddress}</code>
                        <div>
                          <button type="button" onClick={() => copyAddress(row.settlementVaultAddress!)}><Copy size={14} />{copiedAddress === row.settlementVaultAddress ? "已复制" : "复制地址"}</button>
                          <a href={`${explorerOrigin}/address/${row.settlementVaultAddress}`} target="_blank" rel="noreferrer"><ExternalLink size={14} />BscScan</a>
                        </div>
                      </> : <strong className="is-risk">尚未绑定合约</strong>}
                    </div>
                    <div className="payout-funding-decision">
                      <StatusPill tone={missingContract ? "danger" : requiresTopUp ? "warning" : "success"}>{missingContract ? "不可调拨" : requiresTopUp ? "需要补入" : "余额充足"}</StatusPill>
                      <small>{row.submittedClaimCount ? `${row.submittedClaimCount} 笔正在链上确认` : row.pendingClaimCount ? "已签发，等待用户提交" : "当前无已绑定 Claim"}</small>
                    </div>
                  </article>
                );
              })}
            </div>
          </>
        )}
      </section>

      <section className="payout-flow-ledger">
        <header>
          <div><p className="eyebrow">PAYOUT AUDIT TRAIL</p><h2>收益兑付流水</h2><span>逐笔追踪收益来源 Vault、配对 USDT 合约、SAGE 销毁、IAGE 复投与用户实际到账。</span></div>
          <ReceiptText />
        </header>
        <div className="payout-flow-toolbar">
          <div role="tablist" aria-label="兑付流水状态">
            {(["ALL", "PENDING", "CONFIRMED", "ATTENTION"] as FlowFilter[]).map((filter) => (
              <button type="button" role="tab" aria-selected={flowFilter === filter} className={flowFilter === filter ? "is-active" : ""} onClick={() => {
                setFlowFilter(filter);
                setPayoutFlowPage(1);
              }} key={filter}>
                {{ ALL: "全部", PENDING: "待处理", CONFIRMED: "已完成", ATTENTION: "需关注" }[filter]}
              </button>
            ))}
          </div>
          <label><span>搜索钱包、Claim 或交易哈希</span><input value={flowSearch} onChange={(event) => {
            setFlowSearch(event.target.value);
            setPayoutFlowPage(1);
          }} placeholder="0x… / Pair 1" /></label>
        </div>

        {payoutOperations.isLoading ? <div className="payout-flow-empty">正在读取兑付流水…</div>
          : payoutOperations.isError ? <div className="payout-flow-empty is-error">流水读取失败，请刷新后重试。</div>
          : filteredPayoutFlows.length ? <div className="payout-flow-table">
            <div className="payout-flow-table-head"><span>时间 / 状态</span><span>用户 / 类型</span><span>Vault 路径</span><span>代币处理</span><span>USDT 兑付</span><span>链上凭证</span></div>
            {paginatedPayoutFlows.map((flow) => {
              const status = flowStatus(Number(flow.flow_status));
              const isUsdtFlow = flow.flow_kind === "SAGE_USDT";
              return <article key={`${flow.flow_kind}:${flow.flow_id}`}>
                <div className="payout-flow-time"><StatusPill tone={status.tone}>{status.label}</StatusPill><strong>{new Date(flow.confirmed_at ?? flow.submitted_at ?? flow.prepared_at).toLocaleString("zh-CN", { hour12: false })}</strong><small>Pair {flow.vault_id} · {flow.reward_type === 1 ? "静态收益" : "动态收益"}</small></div>
                <div className="payout-flow-user"><code title={flow.user_address}>{compactAddress(flow.user_address)}</code><span>{isUsdtFlow ? "SAGE → USDT" : "IAGE 平台复投"}</span><button type="button" onClick={() => copyAddress(flow.user_address)}><Copy size={13} />{copiedAddress === flow.user_address ? "已复制" : "复制钱包"}</button></div>
                <div className="payout-flow-path"><span><small>收益 Vault</small><code title={flow.payout_vault_address}>{compactAddress(flow.payout_vault_address)}</code></span><i>→</i><span><small>{isUsdtFlow ? "USDT 兑付合约" : "不经过兑付合约"}</small><code title={flow.settlement_vault_address ?? undefined}>{compactAddress(flow.settlement_vault_address)}</code></span></div>
                <div className="payout-flow-token"><span><small>SAGE 销毁</small><strong>{amount(flow.sage_amount)}</strong></span><span><small>IAGE {isUsdtFlow ? "不提现" : "复投"}</small><strong>{amount(flow.iage_amount)}</strong></span></div>
                <div className="payout-flow-usdt"><span><small>毛额 / 手续费</small><strong>{amount(flow.expected_usdt_gross)} / {amount(flow.actual_usdt_fee ?? flow.expected_usdt_fee)}</strong></span><span><small>实际到账</small><strong>{isUsdtFlow ? `${amount(flow.actual_usdt_net ?? flow.expected_usdt_net)} USDT` : "不产生 USDT"}</strong></span></div>
                <div className="payout-flow-proof"><code title={flow.flow_id}>{compactAddress(flow.flow_id)}</code>{flow.tx_hash ? <a href={`${explorerOrigin}/tx/${flow.tx_hash}`} target="_blank" rel="noreferrer"><ExternalLink size={14} />查看交易</a> : <span>等待交易提交</span>}</div>
              </article>;
            })}
            <PaginationBar
              page={payoutFlowCurrentPage}
              pageSize={PAYOUT_FLOW_PAGE_SIZE}
              total={filteredPayoutFlows.length}
              itemLabel="笔流水"
              onPageChange={setPayoutFlowPage}
            />
          </div> : <div className="payout-flow-empty">当前筛选条件下没有兑付流水。</div>}
      </section>

      <footer className="market-data-freshness"><CheckCircle2 size={14} />数据更新于 {new Date(data.updatedAt).toLocaleString("zh-CN", { hour12: false })}</footer>
    </div>
  );
}
